import React from 'react';
import { CallState } from '../../types';
import { MicIcon, MicOffIcon, PhoneOffIcon, SparklesIcon, RadioDotIcon } from '../common/Icons';

interface FloatingCallDockProps {
  callState: CallState;
  isMuted: boolean;
  errorMessage?: string | null;
  waveformHeights: number[];
  onStartCall: () => void;
  onEndCall: () => void;
  onToggleMute: () => void;
  onInterrupt: () => void;
  onResume: () => void;
  onOpenSaveModal?: () => void;
  onQuickPrompt?: (promptText: string, filterArea?: string) => void;
}

export const FloatingCallDock: React.FC<FloatingCallDockProps> = ({
  callState,
  isMuted,
  errorMessage,
  waveformHeights,
  onStartCall,
  onEndCall,
  onToggleMute,
  onInterrupt,
  onResume
}) => {
  const isCallActive = callState !== 'idle' && callState !== 'error';

  // Full-bar tap action
  const handleDockTap = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    if (!isCallActive) {
      onStartCall();
      return;
    }

    if (callState === 'speaking') {
      onInterrupt();
    } else if (callState === 'paused_inactivity') {
      onResume();
    } else if (callState === 'listening') {
      onInterrupt();
    }
  };

  const getStateBadge = () => {
    switch (callState) {
      case 'connecting':
        return (
          <span className="state-badge thinking">
            Connecting<span className="dock-mobile-hide"> Gemini 3.8 Live</span>...
          </span>
        );
      case 'listening':
        return (
          <span className="state-badge listening" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            {isMuted ? (
              <span>Mic Muted</span>
            ) : (
              <>
                <RadioDotIcon size={11} />
                <span>
                  Listening<span className="dock-mobile-hide"> (Urdu / English)</span>
                </span>
              </>
            )}
          </span>
        );
      case 'thinking':
        return <span className="state-badge thinking">Concierge Thinking...</span>;
      case 'speaking':
        return (
          <span className="state-badge speaking" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            <SparklesIcon size={11} />
            <span>
              Concierge Speaking<span className="dock-mobile-hide"> • Tap to Interrupt</span>
            </span>
          </span>
        );
      case 'paused_inactivity':
        return (
          <span
            className="state-badge"
            style={{
              color: 'hsl(var(--warning))',
              backgroundColor: 'hsl(var(--warning) / 0.14)',
              border: 'none',
              boxShadow: 'inset 0 1px 0 hsl(var(--warning) / 0.28)'
            }}
          >
            Auto-Paused<span className="dock-mobile-hide"> • Tap to Resume</span>
          </span>
        );
      case 'error':
        return (
          <span
            className="state-badge"
            style={{
              color: 'hsl(var(--danger))',
              backgroundColor: 'hsl(var(--danger) / 0.14)',
              border: 'none',
              boxShadow: 'inset 0 1px 0 hsl(var(--danger) / 0.28)'
            }}
          >
            Disconnected
          </span>
        );
      default:
        return <span className="state-badge">Gemini 3.8 Live Ready</span>;
    }
  };

  return (
    <div className="floating-dock-wrapper">
      {errorMessage && !isCallActive && (
        <div
          style={{
            padding: '0.45rem 1rem',
            borderRadius: '9999px',
            backgroundColor: 'hsl(var(--danger) / 0.2)',
            border: 'none',
            boxShadow: 'inset 0 1px 0 hsl(var(--danger) / 0.35), 0 8px 24px hsl(var(--obsidian-950) / 0.35)',
            color: 'hsl(var(--porcelain-50))',
            fontSize: '0.76rem',
            fontWeight: 600,
            backdropFilter: 'blur(16px)'
          }}
        >
          {errorMessage}
        </div>
      )}

      {!isCallActive ? (
        <div
          className="floating-call-dock"
          onClick={handleDockTap}
          role="button"
          tabIndex={0}
          aria-label="Start Voice Call"
        >
          {/* Left: Official Google Gemini 4-Pointed Curved Star Emblem */}
          <div className="dock-brand-group" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', minWidth: 0, flex: '1 1 auto' }}>
            <span className="dock-gemini-orb">
              <SparklesIcon size={20} />
            </span>
            <div className="dock-brand-text" style={{ display: 'flex', flexDirection: 'column', gap: '0.08rem', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0 }}>
                <span
                  className="dock-title-label"
                  style={{
                    fontSize: '0.84rem',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: 'hsl(var(--text-primary))',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Ask Leda • AI Voice Concierge
                </span>
                <span className="dock-live-pill dock-mobile-hide">Gemini Live</span>
              </div>
              <span
                className="dock-mobile-hide"
                style={{
                  fontSize: '0.72rem',
                  color: 'hsl(var(--text-secondary))',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                Speak naturally in Urdu or English
              </span>
            </div>
          </div>

          {/* Center: Ambient Multicolor Gemini Equalizer Bars */}
          <div className="waveform-container" aria-hidden="true" style={{ flex: '0 0 auto', maxWidth: '5.5rem' }}>
            {waveformHeights.map((_h, i) => (
              <span
                key={i}
                className="waveform-bar"
                style={{
                  height: `${6 + ((i * 3) % 7)}px`,
                  opacity: 0.65
                }}
              />
            ))}
          </div>

          {/* Right: Sleek Gemini Live Mic Trigger */}
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <button
              type="button"
              className="dock-voice-cta"
              aria-label="Start Voice Call"
              title="Start Voice Call"
              onClick={(e) => {
                e.stopPropagation();
                onStartCall();
              }}
            >
              <span className="dock-voice-cta-mic">
                <MicIcon size={16} />
              </span>
            </button>
          </div>
        </div>
      ) : (
        <div
          className="floating-call-dock active-call"
          onClick={handleDockTap}
          role="button"
          tabIndex={0}
          aria-label={
            callState === 'speaking'
              ? 'Tap to interrupt speech'
              : callState === 'paused_inactivity'
              ? 'Tap to resume consultation'
              : 'Tap to finish speech turn'
          }
        >
          {/* Left: End Call button + Active Gemini Star */}
          <div className="dock-active-left" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <button
              id="end-call"
              type="button"
              className="dock-action-btn btn-danger"
              onClick={(e) => {
                e.stopPropagation();
                onEndCall();
              }}
              title="End Call"
              aria-label="End Call"
            >
              <PhoneOffIcon size={16} />
            </button>
            <span className="dock-gemini-orb active">
              <SparklesIcon size={20} active />
            </span>
          </div>

          {/* Call state status */}
          <div className="dock-active-status" style={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
            {getStateBadge()}
          </div>

          {/* Center: Animated 4-Color Google/Gemini Frequency Equalizer Bars */}
          <div className="waveform-container" aria-label="Audio Visualizer">
            {waveformHeights.map((h, i) => (
              <span
                key={i}
                className={`waveform-bar ${
                  callState === 'speaking' ? 'speaking' : callState === 'thinking' ? 'thinking' : ''
                }`}
                style={{
                  height: `${isMuted ? 4 : h}px`
                }}
              />
            ))}
          </div>

          {/* Right: Mic Toggle */}
          <div className="dock-active-right" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
            <button
              id="mic-toggle"
              type="button"
              className={`dock-action-btn ${isMuted ? 'btn-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleMute();
              }}
              title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
              aria-label={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
            >
              {isMuted ? <MicOffIcon size={16} /> : <MicIcon size={16} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
